import React from 'react';
import styled from 'styled-components';
import {
  IoClose,
  IoGrid,
  IoFolderOpen,
  IoPeople,
  IoTrendingUp,
  IoDocument,
  IoTime,
  IoSettings,
  IoCalendar,
  IoBarChart,
  IoLogOut,
  IoLogIn
} from 'react-icons/io5';
import { Button, IconButton } from '../Components/UI/Buttons';
import { FlexContainer, Overlay } from '../Components/UI/Containers';
import { Text } from '../Components/UI/Typography';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Context/AuthContext';
import PropTypes from 'prop-types';

// Modern minimalist sidebar container
const SidebarContainer = styled.div`
  position: fixed;
  height: 100vh;
  top: 0;
  left: 0;
  width: 280px;
  background: linear-gradient(135deg, 
    ${props => props.theme.colors.background} 0%, 
    ${props => props.theme.colors.backgroundSecondary || props.theme.colors.background} 100%
  );
  backdrop-filter: blur(10px);
  border-right: 1px solid ${props => props.theme.colors.border}20;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transform: translateX(${props => (props.isOpen ? '0' : '-100%')});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: ${props => props.theme.typography.fontFamily};
  
  @media (max-width: 768px) {
    width: 85%;
    max-width: 320px;
  }
`;

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.lg};
  overflow-y: auto;
  
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.border};
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.primary}40;
  }
`;

const SidebarHeader = styled(FlexContainer).attrs({
  justify: 'space-between',
  align: 'center'
})`
  margin-bottom: ${props => props.theme.spacing.xl};
  padding-bottom: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border}20;
`;

const SidebarLogo = styled.img`
  height: 24px;
  width: auto;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  margin-left: ${props => props.theme.spacing.lg};
  
  &:hover {
    transform: scale(1.05);
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15));
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const CloseButton = styled(IconButton)`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: ${props => props.theme.colors.text.secondary};
  background: transparent;
  border: 1px solid ${props => props.theme.colors.border}30;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover:not(:disabled) {
    color: ${props => props.theme.colors.error};
    background: ${props => props.theme.colors.error}10;
    border-color: ${props => props.theme.colors.error}30;
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const MenuSection = styled.div`
  flex: 1;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xs};
  border-radius: ${props => props.theme.borderRadius.large};
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  color: ${props => props.theme.colors.text.primary};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 0;
    height: 100%;
    background: linear-gradient(90deg, 
      ${props => props.theme.colors.primary}15, 
      transparent
    );
    transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  &:hover {
    background: ${props => props.theme.colors.primary}08;
    color: ${props => props.theme.colors.primary};
    transform: translateX(8px);
    
    &::before {
      width: 100%;
    }
  }

  &:active {
    transform: translateX(4px) scale(0.98);
  }
  
  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary}50;
    outline-offset: 2px;
  }
`;

const MenuIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: inherit;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${MenuItem}:hover & {
    transform: scale(1.1);
  }
`;

const MenuText = styled(Text).attrs({
  size: 'sm',
  weight: 'medium'
})`
  margin: 0;
  color: inherit;
  font-family: ${props => props.theme.typography.fontFamily};
  letter-spacing: 0.5px;
`;

const MobileSection = styled.div`
  margin-top: auto;
  padding-top: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border}20;
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const UserInfo = styled(FlexContainer).attrs({
  direction: 'column',
  gap: 'md'
})``;

const UserWelcome = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.md};
  background: linear-gradient(135deg, 
    ${props => props.theme.colors.primary}10, 
    ${props => props.theme.colors.primary}05
  );
  border-radius: ${props => props.theme.borderRadius.large};
  border: 1px solid ${props => props.theme.colors.primary}20;
`;

const UserName = styled(Text).attrs({
  size: 'sm',
  weight: 'semibold'
})`
  margin: 0;
  color: ${props => props.theme.colors.primary};
  font-family: ${props => props.theme.typography.fontFamily};
`;

const ActionButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  width: 100%;
  height: 44px;
  border-radius: ${props => props.theme.borderRadius.large};
  font-weight: 500;
  letter-spacing: 0.5px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${props =>
    props.variant === 'error'
      ? `${props.theme.colors.error}30`
      : `${props.theme.colors.primary}30`
  };
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Menu configuration with enhanced icons
const menuConfig = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: <IoGrid size={20} />,
    path: '/dashboard',
    adminOnly: false
  },
  {
    id: 'site-management',
    label: 'Sites',
    icon: <IoFolderOpen size={20} />,
    path: '/sitemanagement/register',
    adminOnly: true
  },
  {
    id: 'user-management',
    label: 'Users',
    icon: <IoPeople size={20} />,
    path: '/usermanagement',
    adminOnly: true
  },
  {
    id: 'department-management',
    label: 'Departments',
    icon: <IoTime size={20} />,
    path: '/departmentmanagement',
    adminOnly: true
  },
  {
    id: 'leaves',
    label: 'Leaves',
    icon: <IoDocument size={20} />,
    path: '/leaves',
    adminOnly: true
  },
  {
    id: 'attendance-records',
    label: 'Attendance',
    icon: <IoTime size={20} />,
    path: '/attendance/daily-records',
    adminOnly: true,
    children: [
      { id: 'attendance-settings', label: 'Settings', path: '/attendance/settings', adminOnly: true, icon: <IoSettings size={16} /> },
      { id: 'attendance-daily', label: 'Daily Records', path: '/attendance/daily-records', adminOnly: true, icon: <IoCalendar size={16} /> },
      { id: 'attendance-monthly', label: 'Monthly Reports', path: '/attendance/monthly-reports', adminOnly: true, icon: <IoBarChart size={16} /> },
      { id: 'attendance-corrections', label: 'Corrections', path: '/attendance/corrections', adminOnly: true, icon: <IoTime size={16} /> }
    ]
  }
];

const Sidebar = ({
  isOpen = false,
  toggleSidebar,
  isLoggedIn = false,
  userName = 'User',
  onLogout
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Memoized menu items based on user role
  const visibleMenuItems = React.useMemo(() => {
    if (!user?.role) return [];

    const isAdmin = user.role === 'Admin';
    return menuConfig.filter(item => !item.adminOnly || isAdmin);
  }, [user]);

  // Track which menu items are expanded (for items with children)
  const [expandedIds, setExpandedIds] = React.useState(() => ({}));

  const toggleExpand = React.useCallback((id) => {
    setExpandedIds(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  // Event handlers
  const handleMenuItemClick = React.useCallback((path) => {
    navigate(path);
    toggleSidebar();
  }, [navigate, toggleSidebar]);

  const handleLogoClick = React.useCallback(() => {
    navigate('/dashboard');
    toggleSidebar();
  }, [navigate, toggleSidebar]);

  const handleLogout = React.useCallback(() => {
    onLogout?.();
    toggleSidebar();
  }, [onLogout, toggleSidebar]);

  const handleLogin = React.useCallback(() => {
    navigate('/login');
    toggleSidebar();
  }, [navigate, toggleSidebar]);

  const handleKeyDown = React.useCallback((e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  }, []);

  return (
    <>
      <Overlay
        isOpen={isOpen}
        onClick={toggleSidebar}
        style={{
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
          transition: 'opacity 0.3s ease, visibility 0.3s ease'
        }}
      />

      <SidebarContainer isOpen={isOpen}>
        <SidebarContent>
          <SidebarHeader>
            <SidebarLogo
              src="/assets/white-background-removebg-preview.png"
              alt="Company Logo"
              onClick={handleLogoClick}
              title="Go to Dashboard"
            />
            <CloseButton
              onClick={toggleSidebar}
              aria-label="Close navigation menu"
              title="Close Menu"
            >
              <IoClose size={20} />
            </CloseButton>
          </SidebarHeader>

          <MenuSection>
            {visibleMenuItems.map(item => (
              <div key={item.id}>
                <MenuItem
                  onClick={() => {
                    if (Array.isArray(item.children) && item.children.length > 0) {
                      toggleExpand(item.id);
                    } else {
                      handleMenuItemClick(item.path);
                    }
                  }}
                  onKeyDown={(e) => handleKeyDown(e, () => {
                    if (Array.isArray(item.children) && item.children.length > 0) toggleExpand(item.id);
                    else handleMenuItemClick(item.path);
                  })}
                  role="button"
                  tabIndex={0}
                  aria-label={`Navigate to ${item.label}`}
                  aria-expanded={Array.isArray(item.children) && item.children.length > 0 ? !!expandedIds[item.id] : undefined}
                >
                  <MenuIcon>{item.icon}</MenuIcon>
                  <MenuText>{item.label}</MenuText>
                </MenuItem>
                {Array.isArray(item.children) && item.children.length > 0 && (
                  expandedIds[item.id] ? (
                    <div style={{ paddingLeft: '1.5rem', marginTop: '0.25rem' }}>
                      {item.children.map(child => (
                        <MenuItem
                          key={child.id}
                          onClick={() => handleMenuItemClick(child.path)}
                          onKeyDown={(e) => handleKeyDown(e, () => handleMenuItemClick(child.path))}
                          role="button"
                          tabIndex={0}
                          aria-label={`Navigate to ${child.label}`}
                          style={{ padding: '8px 16px', fontSize: '0.95rem', alignItems: 'center' }}
                        >
                          <MenuIcon style={{ width: 20, height: 20 }}>{child.icon}</MenuIcon>
                          <MenuText>{child.label}</MenuText>
                        </MenuItem>
                      ))}
                    </div>
                  ) : null
                )}
              </div>
            ))}
          </MenuSection>

          <MobileSection>
            <UserInfo>
              {isLoggedIn ? (
                <>
                  <UserWelcome>
                    <UserName>Welcome back,</UserName>
                    <UserName>{userName}</UserName>
                  </UserWelcome>
                  <ActionButton
                    variant="error"
                    size="small"
                    onClick={handleLogout}
                    aria-label="Logout from account"
                  >
                    <IoLogOut size={18} />
                    Sign Out
                  </ActionButton>
                </>
              ) : (
                <ActionButton
                  onClick={handleLogin}
                  aria-label="Go to login page"
                >
                  <IoLogIn size={18} />
                  Sign In
                </ActionButton>
              )}
            </UserInfo>
          </MobileSection>
        </SidebarContent>
      </SidebarContainer>
    </>
  );
};

// PropTypes for production safety
Sidebar.propTypes = {
  isOpen: PropTypes.bool,
  toggleSidebar: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool,
  userName: PropTypes.string,
  onLogout: PropTypes.func
};

export default React.memo(Sidebar);
